import { useCallback } from 'react';

import { useParams } from 'react-router';

import { useWidgetAnimation } from '@/features/Dashboard/context/WidgetAnimation/hooks';
import {
  buildUpdatedTabContent,
  getWidgetAnimationIds
} from '@/features/Dashboard/hooks/useBuildDashboardStructure.utils';
import { useCurrentDashboardConfig } from '@/features/Dashboard/hooks/useCurrentDashboardConfig';
import {
  getEffectiveTabName,
  getTabContent,
  normalizeTabEntry
} from '@/features/Dashboard/lib/transformers';
import { selectInitialDashboardName } from '@/features/Dashboard/pages/Edit/model/dashboardEdit.selectors';
import type { DashboardStructure, MarketApp, MarketApps } from '@/features/Dashboard/types';
import { useAppSelector } from '@/hooks/redux-hooks';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

export const useBuildDashboardStructure = () => {
  const { setElementsToAnimateId } = useWidgetAnimation();
  const params = useParams();
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const initialDashboardNameFromRedux = useAppSelector(selectInitialDashboardName);
  const initialDashboardName =
    initialDashboardNameFromRedux || (dashboardId ? decodeURIComponent(dashboardId) : '');
  const allDashboardTabs = useCurrentDashboardConfig() ?? {};
  const currentTabName = params.tabName ? decodeURIComponent(params.tabName) : undefined;
  return useCallback(
    (widget: MarketApp) => {
      const updatedTabs: Record<string, { id: string; content: MarketApps }> = {};

      const effectiveTabName = getEffectiveTabName(currentTabName);
      const hasAnyTabs = Object.keys(allDashboardTabs).length > 0;
      const widgetAnimationIds = getWidgetAnimationIds(widget);

      if (hasAnyTabs) {
        const tabNames = new Set(Object.keys(allDashboardTabs));

        // First, copy/transform all existing tabs
        for (const [tabName, tabValue] of Object.entries(allDashboardTabs)) {
          const normalized = normalizeTabEntry(tabName, tabValue);
          if (tabName === effectiveTabName) {
            // Update current tab with new widget
            const currentTabContent = getTabContent(tabValue);
            const updatedContent = buildUpdatedTabContent(currentTabContent, widget);
            setElementsToAnimateId(widgetAnimationIds);

            updatedTabs[tabName] = {
              id: normalized.id,
              content: updatedContent
            };
          } else {
            // Preserve other tabs as-is
            updatedTabs[tabName] = {
              id: normalized.id,
              content: getTabContent(tabValue)
            };
          }
        }

        // If the effective tab didn't exist, create it now
        if (!tabNames.has(effectiveTabName)) {
          setElementsToAnimateId(widgetAnimationIds);
          updatedTabs[effectiveTabName] = {
            id: generateUniqueUUID(new Set()),
            content: [widget] as MarketApps
          };
        }
      } else {
        // Empty dashboard: create the first tab and place the widget
        setElementsToAnimateId(widgetAnimationIds);

        updatedTabs[effectiveTabName] = {
          id: generateUniqueUUID(new Set()),
          content: [widget] as MarketApps
        };
      }

      const structurePayload: DashboardStructure = {
        [initialDashboardName]: updatedTabs
      };
      return structurePayload;
    },
    [allDashboardTabs, currentTabName, initialDashboardName, setElementsToAnimateId]
  );
};
